/**
 * Weekly missions: each client gets one mission from each group every week (Monday 00:00 UTC to the
 * next Monday). Progress is read from what the client actually did on their live account; demo
 * trading never counts. Finishing enough missions in a row of weeks builds a streak.
 */
export const MISSION_GROUPS = ['trading', 'safety', 'explore'];
export const MISSION_GROUP_LABEL = {
    trading: 'Trading',
    safety: 'Safety & skills',
    explore: 'Explore & invite',
};
export const MISSIONS = [
    { id: 'trade_days', group: 'trading', title: 'Trade on {n} different days', text: 'Make a live trade on {n} different days this week.', to: '/trade', cta: 'Trade', unit: 'count', points: 300, target: 3, weight: 2 },
    { id: 'asset_classes', group: 'trading', title: 'Trade {n} asset classes', text: 'Close live positions in {n} different asset classes, such as crypto and indices.', to: '/markets', cta: 'Markets', unit: 'count', points: 250, target: 2, weight: 2 },
    { id: 'closes', group: 'trading', title: 'Close {n} positions', text: 'Close {n} live positions this week.', to: '/trade', cta: 'Trade', unit: 'count', points: 200, target: 5, weight: 1 },
    { id: 'week_volume', group: 'trading', title: 'Reach {n} weekly volume', text: 'Trade {n} in live volume this week.', to: '/trade', cta: 'Trade', unit: 'usd', points: 400, target: 25_000, weight: 1 },
    { id: 'sl_trades', group: 'safety', title: 'Set a stop loss on {n} trades', text: 'Open {n} live positions with a stop loss, so each one can only lose what you planned.', to: '/trade', cta: 'Trade', unit: 'count', points: 300, target: 3, weight: 3 },
    { id: 'tp_sl', group: 'safety', title: 'Use take profit and stop loss together', text: 'Open a live position with both a take profit and a stop loss.', to: '/trade', cta: 'Trade', unit: 'count', points: 200, target: 1, weight: 2 },
    { id: 'pending_fill', group: 'safety', title: 'Trade with a limit or stop order', text: 'Have a limit or stop order filled on your live account.', to: '/trade', cta: 'Trade', unit: 'count', points: 150, target: 1, weight: 2 },
    { id: 'new_instrument', group: 'explore', title: "Trade a market you haven't traded", text: 'Make your first live trade on a market that is new to you.', to: '/markets', cta: 'Markets', unit: 'count', points: 200, target: 1, weight: 2 },
    { id: 'yield_stake', group: 'explore', title: 'Start a Yield plan', text: 'Put some of your balance to work in a Yield plan.', to: '/earn/yield', cta: 'Yield', unit: 'count', points: 250, target: 1, weight: 1 },
    { id: 'friend_joins', group: 'explore', title: 'A friend joins with your link', text: 'Get a friend to sign up with your referral link this week.', to: '/earn/referrals', cta: 'Invite', unit: 'count', points: 500, target: 1, weight: 2 },
    { id: 'friend_trades', group: 'explore', title: 'A friend makes their first trade', text: 'One of your friends makes their first live trade this week.', to: '/earn/referrals', cta: 'Invite', unit: 'count', points: 750, target: 1, weight: 1 },
];
export const missionDef = (id) => MISSIONS.find((m) => m.id === id);
export const DEFAULT_MISSION_SETTINGS = {
    pool: Object.fromEntries(MISSIONS.map((m) => [m.id, { enabled: true, points: m.points, target: m.target, weight: m.weight, excludedCountries: [] }])),
    streakNeeded: 2,
    streakBonusPct: 2.5,
    streakMaxPct: 25,
    weeklyCap: 3000,
    minHoldSeconds: 120,
};
/** Returns an error message, or null when the mission settings are valid. */
export function validateMissionSettings(s) {
    for (const m of MISSIONS) {
        const c = s.pool?.[m.id];
        if (!c)
            return `${m.id}: missing`;
        if (!(Number.isInteger(c.points) && c.points >= 0 && c.points <= 1_000_000))
            return `${missionTitle(m, c.target)}: points must be a whole number, 0 or more`;
        if (!(c.target > 0 && c.target <= 1_000_000_000))
            return `${missionTitle(m, c.target)}: target must be above 0`;
        if (m.unit === 'count' && !Number.isInteger(c.target))
            return `${missionTitle(m, c.target)}: target must be a whole number`;
        if (!(Number.isInteger(c.weight) && c.weight >= 0 && c.weight <= 100))
            return `${missionTitle(m, c.target)}: weight must be 0–100`;
        if (!Array.isArray(c.excludedCountries) || c.excludedCountries.some((x) => !/^[A-Z]{2}$/.test(x)))
            return `${missionTitle(m, c.target)}: countries must be 2-letter codes like GB`;
    }
    for (const g of MISSION_GROUPS) {
        if (!MISSIONS.some((m) => m.group === g && s.pool[m.id].enabled && s.pool[m.id].weight > 0))
            return `${MISSION_GROUP_LABEL[g]}: keep at least one mission on with a weight above 0`;
    }
    if (!(Number.isInteger(s.streakNeeded) && s.streakNeeded >= 1 && s.streakNeeded <= MISSION_GROUPS.length))
        return `Streak: missions needed must be 1–${MISSION_GROUPS.length}`;
    if (!(s.streakBonusPct >= 0 && s.streakBonusPct <= 100))
        return 'Streak bonus per week must be 0–100%';
    if (!(s.streakMaxPct >= 0 && s.streakMaxPct <= 500))
        return 'Maximum streak bonus must be 0–500%';
    if (!(Number.isInteger(s.weeklyCap) && s.weeklyCap >= 0))
        return 'Weekly cap must be a whole number, 0 or more';
    if (!(Number.isInteger(s.minHoldSeconds) && s.minHoldSeconds >= 0 && s.minHoldSeconds <= 86_400))
        return 'Minimum hold time must be 0–86,400 seconds';
    return null;
}
const usdShort = (n) => (n >= 1_000_000 ? `$${n / 1_000_000}M` : n >= 1000 ? `$${n / 1000}K` : `$${n}`);
const fill = (s, m, target) => s.replace(/\{n\}/g, m.unit === 'usd' ? usdShort(target) : String(target));
export const missionTitle = (m, target) => fill(m.title, m, target);
export const missionText = (m, target) => fill(m.text, m, target);
const DAY_MS = 86_400_000;
/** Monday 00:00 UTC of the week holding `t`, as YYYY-MM-DD. */
export function weekOf(t) {
    const d = new Date(t);
    const day = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const dow = (new Date(day).getUTCDay() + 6) % 7; // Monday = 0
    return new Date(day - dow * DAY_MS).toISOString().slice(0, 10);
}
export const weekStart = (week) => new Date(`${week}T00:00:00Z`);
export const weekEnd = (week) => new Date(weekStart(week).getTime() + 7 * DAY_MS);
export const addWeeks = (week, n) => new Date(weekStart(week).getTime() + n * 7 * DAY_MS).toISOString().slice(0, 10);
//# sourceMappingURL=missions.js.map